import { createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";

import { useLocation } from "react-router-dom";

const getSessionData = (key) => {
  try {
    return JSON.parse(sessionStorage.getItem(key)) || [];
  } catch {
    return [];
  }
};

const getSessionCart = () => {
  try {
    return JSON.parse(sessionStorage.getItem("CartData")) || [];
  } catch {
    return [];
  }
};

const getArchivedSetIds = () => {
  try {
    return JSON.parse(localStorage.getItem("archivedDesigns")) || [];
  } catch {
    return [];
  }
};

/* ================= CART SLICE ================= */

const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartData: getSessionData("CartData"),
    meltingCartData: [],
    recentlyRemoved: null,
  },
  reducers: {
    initCart: (state) => {
      state.cartData = getSessionCart();
      state.meltingCartData = JSON.parse(sessionStorage.getItem("CartData")) || [];
    },
    addToMeltingCart: (state, action) => {
      const product = action.payload;
      const exists = state.cartData.some((item) => item.jewelCode === product.jewelCode);
      if (exists) return;
      state.cartData.push({ ...product, isAddedInCart: true });
      sessionStorage.setItem("CartData", JSON.stringify(state.cartData));
    },
    addToCart: (state, action) => {
      const { product, location, multipleDesigns } = action.payload;


      // Check the flow type
      const isPlacingOrder = location?.state?.newName === "Create Order" || location?.state?.crmSource || multipleDesigns;

      const isMeltingStock = location?.state?.newName === "Create Melting Stock";

      const exists = state.cartData.some((item) => item.styleNumber === product.styleNumber);

      if (exists && !isPlacingOrder) return;

      const cartItem = {
        ...product,
        isAddedInCart: true,
        rhodium: "W",
        itemPoNo: "",
      };

      if (isPlacingOrder) {
        cartItem.cartItemId = uuidv4();
      }

      state.cartData.push(cartItem);

      sessionStorage.setItem("CartData", JSON.stringify(state.cartData));
    },

    removeFromCart: (state, action) => {
      const product = action.payload;

      const index = state.cartData.findIndex((item) =>
        product.cartItemId
          ? item.cartItemId === product.cartItemId
          : item.styleNumber === product.styleNumber
      );

      if (index === -1) return;

      state.cartData.splice(index, 1);

      state.recentlyRemoved = product;

      sessionStorage.setItem("CartData", JSON.stringify(state.cartData));
    },
    removeFromMeltingCart: (state, action) => {
      const product = action.payload;
      state.cartData = state.cartData.filter((item) => item.jewelCode !== product.jewelCode);
      state.recentlyRemoved = product;
      sessionStorage.setItem("CartData", JSON.stringify(state.cartData));
    },

    clearCart: (state) => {
      state.cartData = [];
      sessionStorage.removeItem("CartData");
    },
    clearMeltingCart: (state) => {
      state.cartData = [];
      sessionStorage.removeItem("CartData");
    },
    addMultipleToCart: (state, action) => {
      const { newItems, location } = action.payload;

      const isPlacingOrder = location?.state?.newName === "Create Order";

      newItems.forEach((product) => {
        const exists = state.cartData.some((item) => item.styleNumber === product.styleNumber);
        if (!exists) {
          state.cartData.push({
            ...product, isAddedInCart: true, rhodium: "W", itemPoNo: "", cartItemId: isPlacingOrder ? uuidv4() : null,
          });
        }
      });

      sessionStorage.setItem("CartData", JSON.stringify(state.cartData));
    },

    removeMultipleFromCart: (state, action) => {
      const styleNumbersToRemove = action.payload;

      state.cartData = state.cartData.filter((item) => !styleNumbersToRemove.includes(item.styleNumber));

      sessionStorage.setItem("CartData", JSON.stringify(state.cartData));
    },
  },
});

/* ================= EDIT CATEGORY SLICE ================= */

const editCategorySlice = createSlice({
  name: "editCategory",
  initialState: {
    storageKey: null,
    data: [],
  },
  reducers: {
    initEditCategory: (state, action) => {
      const storageKey = action.payload;
      state.storageKey = storageKey;
      state.data = getSessionData(storageKey);
    },

    addEditCategory: (state, action) => {
      const { storageKey, data } = action.payload;

      if (storageKey) {
        state.storageKey = storageKey;
      }

      const exists = state.data.some((item) => item?.styleNumber === data?.styleNumber);
      if (exists) return;

      state.data.push(data);
      sessionStorage.setItem(state.storageKey, JSON.stringify(state.data));
    },

    removeEditCategory: (state, action) => {
      const { storageKey, id } = action.payload;

      state.data = state.data.filter((item) => (item._id || item.id) !== id);

      sessionStorage.setItem(state.storageKey, JSON.stringify(state.data));
    },

    clearEditCategory: (state, action) => {
      state.data = [];
      sessionStorage.removeItem(action.payload);
    },
  },
});

/* ================= ARCHIVE SET SLICE ================= */

const archiveSetSlice = createSlice({
  name: "archiveSet",
  initialState: {
    ids: getArchivedSetIds(),
  },
  reducers: {
    addArchivedSet: (state, action) => {
      const setId = action.payload.toString();

      if (state.ids.includes(setId)) return;

      state.ids.push(setId);

      localStorage.setItem(
        "archivedDesigns",
        JSON.stringify(state.ids)
      );
    },

    removeArchivedSet: (state, action) => {
      const setId = action.payload.toString();

      state.ids = state.ids.filter((id) => id !== setId);

      localStorage.setItem(
        "archivedDesigns",
        JSON.stringify(state.ids)
      );
    },

    clearArchivedSets: (state) => {
      state.ids = [];
      localStorage.removeItem("archivedDesigns");
    },
  },
});


const catalogListNavSlice = createSlice({
  name: "catalogListNav",
  initialState: {
    scrollToCatalogId: null,
  },
  reducers: {
    setCatalogListScrollTargetId: (state, action) => {
      const id = action.payload;
      state.scrollToCatalogId = id != null && id !== "" ? String(id) : null;
    },
    clearCatalogListScrollTargetId: (state) => {
      state.scrollToCatalogId = null;
    },
  },
});

/* ================= EXPORTS ================= */

export const { initCart, addToCart, removeFromCart, clearCart, addMultipleToCart, removeMultipleFromCart, removeFromMeltingCart, clearMeltingCart, addToMeltingCart } = cartSlice.actions;
export const { initEditCategory, addEditCategory, removeEditCategory, clearEditCategory } = editCategorySlice.actions;
export const { addArchivedSet, removeArchivedSet, clearArchivedSets } = archiveSetSlice.actions;
export const { setCatalogListScrollTargetId, clearCatalogListScrollTargetId } = catalogListNavSlice.actions;

export const cartReducer = cartSlice.reducer;
export const editCategoryReducer = editCategorySlice.reducer;
export const archiveSetSliceReducer = archiveSetSlice.reducer;
export const catalogListNavReducer = catalogListNavSlice.reducer;